import React, {useEffect, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';

import {Box, Text, useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Video as BaseVideo} from '@amblique/base/app/components/shared/video';
import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
import {
  getHotspotContentEmbedVideoId,
  getHotspotContentNativeVideoSrc,
  hotspotContentHasLinkedVideo,
  hotspotPanelMobileMediaDiffersFromDesktop,
  resolveHotspotContentImageUrl,
  resolveHotspotPanelDesktopMediaFields,
  resolveHotspotPanelMobileMediaFields,
} from '@amblique/king-living/app/utils/shop-the-look-hotspot-content';

/** Ensures panel rich text stays white over global `.amb-markup` body color. */
const HOTSPOT_PANEL_RICH_TEXT_SX = {
  '& .rich-text-content.amb-markup, & .rich-text-content.amb-markup *': {
    color: '#ffffff !important',
  },
};

const MEDIA_LAYER_BOX_PROPS = {
  bottom: 0,
  h: '100%',
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  w: '100%',
};

function HotspotPanelMediaSource({item, imageField, videoEntry, playNative, styles}) {
  const videoRef = useRef(null);
  const imageUrl = useMemo(() => resolveHotspotContentImageUrl(imageField), [imageField]);
  const nativeVideoSrc = useMemo(() => getHotspotContentNativeVideoSrc(videoEntry), [videoEntry]);
  const hasVideoLink = hotspotContentHasLinkedVideo(videoEntry);
  const showVideo = hasVideoLink || Boolean(nativeVideoSrc);
  const embedVideoId = useMemo(() => getHotspotContentEmbedVideoId(videoEntry), [videoEntry]);

  useEffect(() => {
    const el = videoRef.current;

    if (!el || !nativeVideoSrc) {
      return;
    }

    if (playNative) {
      const playPromise = el.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } else {
      el.pause();
    }
  }, [nativeVideoSrc, playNative]);

  if (showVideo && nativeVideoSrc) {
    return (
      <Box
        ref={videoRef}
        aria-label={item?.title != null ? String(item.title) : 'Hotspot video'}
        as="video"
        controls={false}
        preload="auto"
        src={nativeVideoSrc}
        loop
        muted
        playsInline
        {...styles.hotspotPanelNativeVideo}
      />
    );
  }

  if (showVideo && embedVideoId) {
    return (
      <Box {...styles.hotspotPanelEmbedVideo}>
        <BaseVideo id={embedVideoId} size="cover" readonly />
      </Box>
    );
  }

  if (imageUrl) {
    return (
      <Box
        alt={item?.title != null ? String(item.title) : 'Hotspot'}
        as="img"
        decoding="async"
        loading="eager"
        src={imageUrl}
        {...styles.hotspotPanelImg}
      />
    );
  }

  return <Box {...styles.hotspotPanelMediaFallback} />;
}

HotspotPanelMediaSource.propTypes = {
  imageField: PropTypes.any,
  item: PropTypes.object,
  playNative: PropTypes.bool,
  styles: PropTypes.object.isRequired,
  videoEntry: PropTypes.any,
};

function HotspotPanelMediaSlide({item, isActive, styles}) {
  const isDesktopBp = useBreakpointValue({base: false, md: true});
  const useSplitLayers = hotspotPanelMobileMediaDiffersFromDesktop(item);
  const {desktop: desktopFields, mobile: mobileFields} = useMemo(
    () => ({
      desktop: resolveHotspotPanelDesktopMediaFields(item),
      mobile: resolveHotspotPanelMobileMediaFields(item),
    }),
    [item]
  );
  const playDesktopNative = isActive && isDesktopBp === true;
  const playMobileNative = isActive && isDesktopBp !== true;

  if (!useSplitLayers) {
    return (
      <HotspotPanelMediaSource
        imageField={desktopFields.imageField}
        item={item}
        playNative={isActive}
        styles={styles}
        videoEntry={desktopFields.videoEntry}
      />
    );
  }

  return (
    <>
      <Box {...MEDIA_LAYER_BOX_PROPS} display={{base: 'none', md: 'block'}}>
        <HotspotPanelMediaSource
          imageField={desktopFields.imageField}
          item={item}
          playNative={playDesktopNative}
          styles={styles}
          videoEntry={desktopFields.videoEntry}
        />
      </Box>
      <Box {...MEDIA_LAYER_BOX_PROPS} display={{base: 'block', md: 'none'}}>
        <HotspotPanelMediaSource
          imageField={mobileFields.imageField}
          item={item}
          playNative={playMobileNative}
          styles={styles}
          videoEntry={mobileFields.videoEntry}
        />
      </Box>
    </>
  );
}

HotspotPanelMediaSlide.propTypes = {
  item: PropTypes.object,
  isActive: PropTypes.bool.isRequired,
  styles: PropTypes.object.isRequired,
};

/** Stacked slides: opacity swap so inactive videos stay mounted (less flash). */
function HotspotPanelMediaStack({items, activeIndex, styles}) {
  if (!items?.length) {
    return null;
  }

  return (
    <Box h="100%" minH="100%" position="relative" w="100%">
      {items.map((it, idx) => {
        const key = it?.sys?.id != null ? String(it.sys.id) : `hotspot-media-${idx}`;
        const isActive = idx === activeIndex;

        return (
          <Box
            key={key}
            aria-hidden={!isActive}
            bottom={0}
            h="100%"
            left={0}
            opacity={isActive ? 1 : 0}
            pointerEvents={isActive ? 'auto' : 'none'}
            position="absolute"
            right={0}
            top={0}
            transition="opacity 0.2s ease"
            visibility={isActive ? 'visible' : 'hidden'}
            w="100%"
            zIndex={isActive ? 2 : 0}
          >
            <HotspotPanelMediaSlide isActive={isActive} item={it} styles={styles} />
          </Box>
        );
      })}
    </Box>
  );
}

HotspotPanelMediaStack.propTypes = {
  activeIndex: PropTypes.number.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  styles: PropTypes.object.isRequired,
};

/** HotspotContent flyout: media + rich text (desktop text anchored bottom). */
export function ShopTheLookHotspotPanel({item, items, activeIndex = 0, styles}) {
  const richTextContent =
    item?.text?.json != null ? item.text : item?.body?.json != null ? item.body : null;

  const unsupportedTypename =
    item?.__typename && item.__typename !== 'HotspotContent' && ['Card', 'ProductTiles'].includes(item.__typename);

  const mediaItems = useMemo(() => {
    if (items?.length) {
      return items;
    }

    return item ? [item] : [];
  }, [item, items]);

  if (unsupportedTypename) {
    return (
      <Box {...styles.hotspotPanelRoot} color="white" p={6}>
        <Text fontSize="sm">This hotspot entry type is not supported in the panel.</Text>
      </Box>
    );
  }

  return (
    <Box {...styles.hotspotPanelRoot}>
      <Box {...styles.hotspotPanelMediaShell}>
        <HotspotPanelMediaStack activeIndex={activeIndex} items={mediaItems} styles={styles} />
      </Box>

      <Box {...styles.hotspotPanelTextBlock}>
        {richTextContent ? (
          <Box {...styles.hotspotPanelBody} sx={HOTSPOT_PANEL_RICH_TEXT_SX}>
            <RichTextContent content={richTextContent} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

ShopTheLookHotspotPanel.propTypes = {
  activeIndex: PropTypes.number,
  item: PropTypes.object,
  items: PropTypes.arrayOf(PropTypes.object),
  styles: PropTypes.object.isRequired,
};
